import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock3, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";

const FeedRow = ({ f, onTry, busy }) => (
  <div className="surface flex flex-wrap items-start gap-3 p-4" data-testid={`health-feed-${f.index}`}>
    {f.status === "added" ? <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" /> : <ShieldAlert size={20} className="mt-0.5 shrink-0 text-amber-600" />}
    <div className="min-w-0 flex-1">
      <p className="font-medium">{f.name} <span className="text-sm font-normal text-slate-500">· {f.country}</span></p>
      {f.status === "added" ? (
        <p className="text-sm text-slate-600" data-testid={`health-feed-status-${f.index}`}>Readable and approved{f.active ? "" : " (switched off)"} · {f.records} records{f.last_record_at && ` · latest ${fmtDate(f.last_record_at)}`}</p>
      ) : (
        <p className="text-sm text-slate-600" data-testid={`health-feed-status-${f.index}`}>Not readable yet: {f.error}{f.tried_at && ` · last tried ${fmtDate(f.tried_at)}`}</p>
      )}
      {f.status !== "added" && <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500" data-testid={`health-feed-next-${f.index}`}><Clock3 size={12} />Next automatic try: {f.next_try ? fmtDate(f.next_try) : "with the next hourly refresh"}</p>}
    </div>
    {f.status !== "added" && <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onTry(f)} data-testid={`health-feed-try-${f.index}`}><RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Try now</Button>}
  </div>
);

export default function AdminSourceHealth() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/source-health").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const tryNow = async (f) => {
    setBusy(f.index);
    try {
      const { data } = await api.post(`/admin/source-health/${f.index}/try`);
      data.status === "added" ? toast.success(`${f.name} is readable and now an approved source`) : toast.message(`${f.name} still isn't readable: ${data.error}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  if (!d) return <Spinner />;
  const waiting = d.feeds.filter((f) => f.status !== "added").length;
  return (
    <div className="space-y-5" data-testid="admin-source-health-page">
      <Link to="/admin/sources" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={15} />Approved sources</Link>
      <PageHeader env="admin" title="Source health" subtitle={`Official foreign-ministry feeds for the Taiwan policy positions. Feeds that can't be read are retried every ${d.retry_days} days and added automatically once readable.`} />
      <p className="text-sm text-slate-600" data-testid="health-summary">{d.feeds.length - waiting} of {d.feeds.length} official feeds readable{d.checked_at && ` · last automatic check ${fmtDate(d.checked_at)}`}</p>
      <div className="space-y-3">{d.feeds.map((f) => <FeedRow key={f.index} f={f} busy={busy === f.index} onTry={tryNow} />)}</div>
      <div className="surface p-4" data-testid="health-failed-refresh">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Approved sources that failed in the latest refresh{d.last_refresh_at && ` (${fmtDate(d.last_refresh_at)})`}</p>
        <p className="mt-2 text-sm text-slate-700">{d.failed_last_refresh.length ? d.failed_last_refresh.join(", ") : "None. Every approved feed was read."}</p>
      </div>
    </div>
  );
}
